import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'What to Include on a Freelance Invoice (Legal Requirements)',
      'description': 'Every element a freelance invoice must contain — from legal requirements to professional extras that get you paid faster.',
      'url': 'https://www.invilic.com/blog/what-to-include-freelance-invoice',
      'datePublished': '2025-05-20',
      'dateModified': '2025-05-20',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Do freelancers legally need to issue invoices?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'In most countries, self-employed individuals and freelancers are legally required to provide an invoice for services rendered. The invoice serves as your proof of income for tax purposes and gives the client a record of what they paid for. Even where not legally mandated, invoices protect you in disputes over payment.' },
        },
        {
          '@type': 'Question',
          'name': 'Should a freelance invoice include tax?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'It depends on your location and registration status. In India, you add GST only if you are GST-registered (turnover above ₹20 lakh for services). In the UK, you add VAT only if registered (turnover above £90,000). In the US, sales tax on services is rarely applicable but varies by state. If you are below your country\'s threshold, issue invoices without tax.' },
        },
        {
          '@type': 'Question',
          'name': 'What payment methods should I list on a freelance invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'List whatever payment methods you accept — bank transfer (with your account number, sort code, or IBAN), PayPal email, UPI ID, Wise, or Payoneer. The easier you make it to pay, the faster you get paid. Bank transfers are standard for business clients; digital wallets work well for smaller amounts.' },
        },
        {
          '@type': 'Question',
          'name': 'What should I do if a client does not pay my invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Send a payment reminder email 1 day after the due date (polite), then a firmer follow-up at 7 days, and a final notice at 14–30 days that mentions late payment interest or legal action. Keep all invoice records. If the amount is significant, consider small claims court or a debt collection service after 30–60 days with no response.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const essentials = [
  { field: 'Your full name or business name', why: 'Identifies who issued the invoice — required in every country.' },
  { field: 'Your contact details (email, address)', why: 'Allows the client to reach you with queries and is required for legal documents in most jurisdictions.' },
  { field: 'Client\'s name and contact details', why: 'The invoice must address a specific party — not just "To Whom It May Concern".' },
  { field: 'A unique invoice number', why: 'Enables both you and the client to track this specific invoice. Also required for accounting and tax records.' },
  { field: 'Invoice date', why: 'Records when the invoice was issued — determines when payment terms begin.' },
  { field: 'Due date (or payment terms)', why: 'Makes your payment expectation explicit. "Net 30" or a specific date like "Payment due June 15, 2025".' },
  { field: 'Itemised list of services', why: 'Each line should have a description, quantity (hours or units), rate, and line total. Vague invoices cause disputes.' },
  { field: 'Total amount due', why: 'Clearly stated in the invoice currency. Include tax breakdown separately if applicable.' },
  { field: 'Payment instructions', why: 'Bank account details, UPI, PayPal — whatever method you want to be paid through.' },
];

const extras = [
  { field: 'Your logo', why: 'Builds brand credibility and makes invoices recognisable to repeat clients.' },
  { field: 'Tax number (GSTIN, VAT number)', why: 'Required if you\'re registered for tax. Enables your client to claim input tax credits.' },
  { field: 'Purchase order (PO) number', why: 'Many corporate clients require this. Ask for it before starting work.' },
  { field: 'Late payment terms', why: 'e.g. "Interest of 2% per month applies after the due date." Setting this expectation upfront discourages late payment.' },
  { field: 'Thank you note', why: 'A simple "Thank you for your business" makes you memorable and humanises the transaction.' },
];

export default function FreelanceInvoiceChecklistPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="What to Include on a Freelance Invoice (Legal Requirements) | Invilic Blog"
        description="Every element a freelance invoice must contain — the legal essentials, optional professional extras, and what to leave off completely."
        canonical="https://www.invilic.com/blog/what-to-include-freelance-invoice"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Freelancing</span>
          </div>
          <span className="inline-block bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">Freelancing</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            What to Include on a Freelance Invoice (Legal Requirements)
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 5 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            A freelance invoice must include your name, the client's name, a unique invoice number, the date, an itemised list of services, the total amount due, and your payment details. These are non-negotiable — a document missing any of these is not a valid invoice in most countries.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-5">The 9 Essential Elements</h2>
          <p className="mb-5">These fields are required on every freelance invoice regardless of country or client type:</p>
          <div className="space-y-3 mb-8">
            {essentials.map(({ field, why }) => (
              <div key={field} className="flex gap-3 p-4 border border-gray-100 dark:border-gray-800 rounded-xl">
                <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm mb-0.5">{field}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{why}</p>
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Build a professional freelance invoice in under 2 minutes — all essential fields included by default."
            to="/create/freelancer"
            btnText="Create Freelance Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-5">Optional But Strongly Recommended</h2>
          <p className="mb-5">These elements are not strictly required by law but make your invoice more professional and get you paid faster:</p>
          <div className="space-y-3 mb-8">
            {extras.map(({ field, why }) => (
              <div key={field} className="flex gap-3 p-4 border border-gray-100 dark:border-gray-800 rounded-xl bg-gray-50 dark:bg-gray-900/30">
                <CheckCircle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm mb-0.5">{field}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{why}</p>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What NOT to Include on a Freelance Invoice</h2>
          <p className="mb-4">
            <strong className="font-semibold text-gray-900 dark:text-white">Don't add tax you haven't registered for.</strong> If you are not GST-registered in India or VAT-registered in the UK, do not charge or show any tax on your invoice. Adding tax when you're not registered is illegal in most jurisdictions.
          </p>
          <p className="mb-4">
            <strong className="font-semibold text-gray-900 dark:text-white">Don't use vague line item descriptions.</strong> "Consulting — April" is not a valid line item. "Brand strategy workshop, 3 sessions × 2 hours, April 8–22" is. Vague descriptions are the single biggest cause of payment disputes.
          </p>
          <p className="mb-6">
            <strong className="font-semibold text-gray-900 dark:text-white">Don't forget to re-check the total.</strong> A wrong total on an invoice — even by mistake — looks unprofessional and can cause the client to reject the invoice entirely. Always review the numbers before sending.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">A Simple Invoicing Checklist</h2>
          <p className="mb-4">Before you hit send, confirm every item on this list:</p>
          <div className="p-5 border border-gray-100 dark:border-gray-800 rounded-2xl bg-gray-50 dark:bg-gray-900/30 mb-8">
            <ul className="space-y-2">
              {[
                'Your name / business name is correct',
                'Your contact email and address are included',
                'Client name and address are correct',
                'Invoice number is unique and sequential',
                'Invoice date is today\'s date',
                'Due date or payment terms are clearly stated',
                'Each line item has description, quantity, rate, and total',
                'Grand total is correct and clearly visible',
                'Payment method details are included (bank, UPI, etc.)',
                'Tax is shown only if you are registered',
              ].map(item => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="w-4 h-4 border border-gray-300 dark:border-gray-600 rounded flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a complete, professional freelance invoice in seconds. Every essential field is included — free, no account needed."
            to="/create/freelancer"
            btnText="Create Your Invoice Now"
          />

        </article>
        <RelatedArticles current="/blog/what-to-include-freelance-invoice" />
      </main>
    </div>
  );
}
